import { useState } from 'react';

/** One row slot, as much of it as the reset rule needs. */
export interface LiveFrontCell {
  /** The artifact's stable id (the event id) — NOT a phase-qualified React key. */
  id: string;
  /** Whether this cell is LIVE as of the render that produced it. */
  live: boolean;
}

/**
 * The UNIFORM scroll-reset rule for every live swipe surface: a post-mount reset
 * fires ONLY when a row entry the previous render showed as NOT live arrives at
 * the front as live. An OBSERVED not-live→live transition, in other words —
 * never merely "the front slot happens to be live now".
 *
 * Returns a token to pass as `SwipeRow`'s `startKey`. It increments only when all
 * three hold: the front id CHANGED, the new front is LIVE, and that same id was
 * in the previous render's row as NOT live.
 *
 * Why the whole row and not just the front (PR #1044 review): with only
 * `(frontId, frontIsLive)` the guard cannot tell "a room went live" from "an
 * already-live room slid into slot 0". Under severity-first a live pill is the
 * front slot in the common case, so the second reading became the DEFAULT rather
 * than a corner — the busiest public live room churns, and every churn yanked a
 * reader back to pill 1 with no user action and nothing having gone live. Seeing
 * the previous render's whole row is what makes the distinction expressible: the
 * hook can ask what it knew about this id last time.
 *
 * What that buys, case by case:
 *
 *   - LIVE-BLOCK CHURN never resets. The front live room ends, or a busiest-first
 *     refetch reorders two live rooms, and the next live room takes slot 0: the
 *     arriving id was ALREADY live last render, so there is no transition and the
 *     scroll holds.
 *   - A GENUINE GO-LIVE still resets. A room sitting in the row as `soon` flips
 *     live on a tick and sorts to the front: last render it was known not-live,
 *     this render it is live at the front. That IS the designed new row start —
 *     the one reorder worth showing from the beginning.
 *   - A RE-RANK among non-live entries never resets (nothing live arrived).
 *   - A SAME-ID FLIP never resets: the front slot did not change, it just went
 *     live in place. (Its React key may embed phase and remount the cell; that is
 *     an impression concern, not a scroll one.)
 *   - AN UNSEEN LIVE ID never resets — see below.
 *
 * DELIBERATE CONSERVATISM (PR #1044 review; a behavior change from the #1040-era
 * "any live arrival at the front" rule): a live id the previous render did not
 * carry AT ALL — a brand-new row from a refetch, a first payload, a row that was
 * empty a moment ago — does NOT reset. The hook cannot tell a room that just went
 * live from one that had been live for an hour and merely arrived in the payload
 * late, and the two want opposite treatment. Given the choice between an
 * unnecessary yank and a missed reset, this rule takes the missed reset: the
 * reader keeps their place and the pill is still there to find. Only transitions
 * this surface actually WITNESSED move the row.
 *
 * MOUNT is unaffected: `SwipeRow`'s layout effect asserts `scrollLeft = 0` on
 * mount whatever this value is, so a resumed PWA's browser-preserved scroll
 * offset is still cleared. The first render seeds state from its own cells and
 * never increments.
 *
 * Callers pass a plain projection of their row — `LivePillRow` from its entries,
 * `LiveSpaceSlot` from its cells (the ended-recap cell maps to `live: false`, so
 * a recap swap stays inert). A fresh array per render is fine: the hook keys on a
 * primitive signature of the row's (id, live) pairs, never on identity, so
 * re-rendering an equal row is inert.
 *
 * Implementation is React's documented "adjusting state during render" pattern
 * (the `useStableBySignature` house idiom): a GUARDED render-phase setState on
 * this hook's own state, self-terminating (the store makes the guard false on the
 * immediate re-render), so it is React-Compiler-safe and cannot loop. It is
 * StrictMode-idempotent for the same reason the previous form was — the
 * render-phase update drains before the second invocation, and the token is a
 * pure function of the committed state plus this render's cells.
 */
export function useLiveFrontResetKey(cells: readonly LiveFrontCell[]): number {
  const signature = cells.map(cell => `${cell.id}:${cell.live ? 1 : 0}`).join('|');
  const [state, setState] = useState(() => ({
    signature,
    // Live-by-id for the row we last saw. A missing key on lookup therefore means
    // "this surface has never rendered that id", which is exactly the case the
    // conservative rule declines to reset on.
    liveById: new Map(cells.map(cell => [cell.id, cell.live])),
    frontId: cells.length > 0 ? cells[0].id : null,
    token: 0,
  }));

  if (state.signature === signature) {
    return state.token;
  }

  const frontId = cells.length > 0 ? cells[0].id : null;
  const frontIsLive = cells.length > 0 && cells[0].live;
  // The transition test: front id changed, is live now, and was seen last render
  // as NOT live. `=== false` (rather than `!`) is load-bearing — it separates
  // "was known not-live" from the unseen-id `undefined`, which must NOT reset.
  const observedGoingLive = frontIsLive && state.liveById.get(frontId ?? '') === false;
  const token = frontId !== state.frontId && observedGoingLive ? state.token + 1 : state.token;

  setState({
    signature,
    liveById: new Map(cells.map(cell => [cell.id, cell.live])),
    frontId,
    token,
  });
  return token;
}
